<template lang="pug">
ClientOnly
  .controls(:class="[vertical && 'vertical', mounted && 'mounted']" ref="root")
    button.ctrl(type="button" :aria-label="__(prevLabel)" @click.stop="scroll(-1)" v-if="!beginning")
      Icon(:name="vertical ? 'arrow-up' : 'arrow-left'" fill="#D2D2D2")
    .space
    button.ctrl(type="button" :aria-label="__(nextLabel)" @click.stop="scroll(1)" v-if="!end")
      Icon(:name="vertical ? 'arrow-down' : 'arrow-right'" fill="#D2D2D2")
</template>
<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  vertical: boolean
  targetRef: HTMLElement | null,
  prevLabel?: string,
  nextLabel?: string,
}>(), {
  prevLabel : 'Previous items',
  nextLabel : 'Next items',
})

const emit = defineEmits<{
  scroll: [opts: {
    top?: number,
    left?: number,
    behavior: string,
  }]
}>()

const root = ref<HTMLElement>()

const end = ref<boolean>(false)
const beginning = ref<boolean>(true)
const mounted = ref<boolean>(false)

function scroll(direction: 1 | -1) {
  const offset = props.vertical
    ? props.targetRef!.scrollTop
    : props.targetRef!.scrollLeft
  const opts = {
    [props.vertical ? 'top' : 'left'] : offset + (direction * (props.vertical ? root.value!.clientHeight : root.value!.clientWidth)),
    behavior                          : 'smooth',
  }
  emit('scroll', opts)
}

function onScroll(e: { target: HTMLElement } | Event) {
  const el = e.target as HTMLElement
  const isBeginning = (props.vertical ? el?.scrollTop : el.scrollLeft) === 0
  let isEnd = false
  if (props.vertical) {
    isEnd = el.scrollTop >= (el.scrollHeight - el.offsetHeight)
  } else {
    isEnd = el.scrollLeft >= (el.scrollWidth - el.clientWidth)
  }
  if (isBeginning !== beginning.value) {
    beginning.value = isBeginning
  }
  if (isEnd !== end.value) {
    end.value = isEnd
  }
}

function checkEl() {
  if (props.targetRef) {
    nextTick(() => {
      const t = props.targetRef
      if (t) {
        onScroll({ target: t })
      }
    })
  }
}

watch(() => props.targetRef, () => {
  checkEl()
})

onMounted(() => {
  mounted.value = true
  nextTick(() => checkEl())
})

defineExpose({
  onScroll,
})
</script>
<style lang="scss" scoped>

.controls {
  position: absolute;
  pointer-events: none;
  top: 0;
  left: $space-s;
  bottom: 0;
  right: $space-s;
  justify-content: space-between;
  align-items: center;
  display: none;

  &.mounted {
    display: flex;
  }

  &.outside {
    left: -$space-s;
    right: -$space-s;

    .ctrl {
      opacity: 1;
    }
  }
}

.controls.vertical {
  flex-direction: column;
  right: 0;
  left: 0;

  .ctrl {
    opacity: 0.8;
    border: 1px solid $color-gray;
    padding: $space-xxxs;

    &:hover {
      opacity: 1.0;
      border-color: $color-gray-dark;
    }
  }

  .icon {
    width: 100%;
    height: $space-xs;
  }
}

.ctrl {
  pointer-events: all;
  background-color: $color-gray-x-light;
  border: none;
  padding: $space-xxs;
  opacity: $opacity-disabled;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;

  &:hover {
    opacity: 1;
  }

  .icon {
    height: 18px;
    width: 18px;
  }
}

</style>
